/**
 * Route tree.
 *
 * One application, three portals, split at the route-group boundary and lazily
 * loaded. No user loads more than one portal in a session, so this is the
 * natural code-split point — and it keeps one auth flow, one design system and
 * one deploy. docs/07-frontend.md §2
 *
 * The platform super-admin console is a *separate entry point* from the same
 * repository. Shipping super-admin code to every parent's browser is not a
 * bundle-size problem, it is a blast-radius problem.
 */

import { lazy } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";

import { PortalRedirect, RedirectIfAuthenticated, RequireAuth, RequirePortal } from "./guards";
import { lazyRoute } from "./lazyRoute";
import { ForbiddenPage, NotFoundPage, RouteErrorPage } from "@/features/errors";
import { AppShell } from "@/shared/layouts/AppShell";

const LoginPage = lazy(() =>
  import("@/features/auth").then((m) => ({ default: m.LoginPage })),
);
const CallbackPage = lazy(() =>
  import("@/features/auth").then((m) => ({ default: m.CallbackPage })),
);
const DashboardPage = lazy(() =>
  import("@/features/dashboard").then((m) => ({ default: m.DashboardPage })),
);
const StatusPage = lazy(() =>
  import("@/features/system").then((m) => ({ default: m.StatusPage })),
);
const StudentDashboardPage = lazy(() =>
  import("@/features/students").then((m) => ({ default: m.StudentDashboardPage })),
);
const StudentListPage = lazy(() =>
  import("@/features/students").then((m) => ({ default: m.StudentListPage })),
);
const StudentProfilePage = lazy(() =>
  import("@/features/students").then((m) => ({ default: m.StudentProfilePage })),
);
const StudentFormPage = lazy(() =>
  import("@/features/students").then((m) => ({ default: m.StudentFormPage })),
);
const PracticePage = lazy(() =>
  import("@/features/practice").then((m) => ({ default: m.PracticePage })),
);
const MyPracticePage = lazy(() =>
  import("@/features/practice").then((m) => ({ default: m.MyPracticePage })),
);
const AttemptPage = lazy(() =>
  import("@/features/practice").then((m) => ({ default: m.AttemptPage })),
);
const CompetitionsPage = lazy(() =>
  import("@/features/competitions").then((m) => ({ default: m.CompetitionsPage })),
);
const PayrollPage = lazy(() =>
  import("@/features/payroll").then((m) => ({ default: m.PayrollPage })),
);
const MyPayslipsPage = lazy(() =>
  import("@/features/payroll").then((m) => ({ default: m.MyPayslipsPage })),
);
const AdmissionsPage = lazy(() =>
  import("@/features/admissions").then((m) => ({ default: m.AdmissionsPage })),
);
const TransportPage = lazy(() =>
  import("@/features/transport").then((m) => ({ default: m.TransportPage })),
);
const TimetablePage = lazy(() =>
  import("@/features/timetable").then((m) => ({ default: m.TimetablePage })),
);
const AnnouncementsPage = lazy(() =>
  import("@/features/announcements").then((m) => ({ default: m.AnnouncementsPage })),
);
const ImportPage = lazy(() =>
  import("@/features/imports").then((m) => ({ default: m.ImportPage })),
);
const MarkAttendancePage = lazy(() =>
  import("@/features/attendance").then((m) => ({ default: m.MarkAttendancePage })),
);
const TeacherSectionsPage = lazy(() =>
  import("@/features/attendance").then((m) => ({ default: m.TeacherSectionsPage })),
);
const FamilyAttendancePage = lazy(() =>
  import("@/features/attendance").then((m) => ({ default: m.FamilyAttendancePage })),
);
const FeesDashboardPage = lazy(() =>
  import("@/features/fees").then((m) => ({ default: m.FeesDashboardPage })),
);
const FeeStructuresPage = lazy(() =>
  import("@/features/fees").then((m) => ({ default: m.FeeStructuresPage })),
);
const InvoiceListPage = lazy(() =>
  import("@/features/fees").then((m) => ({ default: m.InvoiceListPage })),
);
const FamilyFeesPage = lazy(() =>
  import("@/features/fees").then((m) => ({ default: m.FamilyFeesPage })),
);
const AcademicSetupPage = lazy(() =>
  import("@/features/academics").then((m) => ({ default: m.AcademicSetupPage })),
);
const StaffListPage = lazy(() =>
  import("@/features/staff").then((m) => ({ default: m.StaffListPage })),
);
const StaffProfilePage = lazy(() =>
  import("@/features/staff").then((m) => ({ default: m.StaffProfilePage })),
);
const StaffFormPage = lazy(() =>
  import("@/features/staff").then((m) => ({ default: m.StaffFormPage })),
);
const GuardianListPage = lazy(() =>
  import("@/features/guardians").then((m) => ({ default: m.GuardianListPage })),
);
const GuardianProfilePage = lazy(() =>
  import("@/features/guardians").then((m) => ({ default: m.GuardianProfilePage })),
);
const AuditTrailPage = lazy(() =>
  import("@/features/audit").then((m) => ({ default: m.AuditTrailPage })),
);
const UsersPage = lazy(() => import("@/features/access").then((m) => ({ default: m.UsersPage })));
const NotificationsPage = lazy(() =>
  import("@/features/notifications").then((m) => ({ default: m.NotificationsPage })),
);
const DeliveryLogPage = lazy(() =>
  import("@/features/notifications").then((m) => ({ default: m.DeliveryLogPage })),
);
const ExamsPage = lazy(() => import("@/features/exams").then((m) => ({ default: m.ExamsPage })));
const MarksEntryPage = lazy(() =>
  import("@/features/exams").then((m) => ({ default: m.MarksEntryPage })),
);
const FamilyResultsPage = lazy(() =>
  import("@/features/exams").then((m) => ({ default: m.FamilyResultsPage })),
);
const AssistantPage = lazy(() => import("@/features/ai").then((m) => ({ default: m.AssistantPage })));
const ProposalsPage = lazy(() => import("@/features/ai").then((m) => ({ default: m.ProposalsPage })));
const AISettingsPage = lazy(() =>
  import("@/features/ai").then((m) => ({ default: m.AISettingsPage })),
);

export const router = createBrowserRouter([
  {
    errorElement: <RouteErrorPage />,
    children: [
      {
        element: <RedirectIfAuthenticated />,
        children: [
          { path: "/login", element: lazyRoute(<LoginPage />) },
          // The identity provider redirects here. Public by necessity: the
          // user has no session yet — establishing one is what this route does.
          // Its path must match OIDC_REDIRECT_URI and the URI registered with
          // the provider, exactly.
          { path: "/auth/callback", element: lazyRoute(<CallbackPage />) },
        ],
      },

      {
        element: <RequireAuth />,
        children: [
          { path: "/", element: <PortalRedirect /> },

          // --- Administration portal ---------------------------------------
          {
            element: <RequirePortal portal="admin" />,
            children: [
              {
                path: "/admin",
                element: <AppShell portal="admin" />,
                children: [
                  { index: true, element: lazyRoute(<DashboardPage portal="admin" />) },
                  { path: "school", element: lazyRoute(<DashboardPage portal="admin" />) },
                  { path: "users", element: lazyRoute(<UsersPage />) },
                  // Order matters: "new" must be matched before ":studentId".
                  { path: "students", element: lazyRoute(<StudentDashboardPage />) },
                  { path: "students/list", element: lazyRoute(<StudentListPage />) },
                  { path: "students/new", element: lazyRoute(<StudentFormPage />) },
                  { path: "students/:studentId", element: lazyRoute(<StudentProfilePage />) },
                  { path: "students/:studentId/edit", element: lazyRoute(<StudentFormPage />) },
                  { path: "imports", element: lazyRoute(<ImportPage />) },
                  { path: "attendance", element: lazyRoute(<MarkAttendancePage />) },
                  { path: "academics", element: lazyRoute(<AcademicSetupPage />) },
                  { path: "timetable", element: lazyRoute(<TimetablePage />) },
                  { path: "transport", element: lazyRoute(<TransportPage />) },
                  { path: "admissions", element: lazyRoute(<AdmissionsPage />) },
                  { path: "payroll", element: lazyRoute(<PayrollPage />) },
                  { path: "competitions", element: lazyRoute(<CompetitionsPage />) },
                  { path: "practice", element: lazyRoute(<PracticePage />) },
                  { path: "my-payslips", element: lazyRoute(<MyPayslipsPage />) },
                  { path: "announcements", element: lazyRoute(<AnnouncementsPage />) },
                  // Order matters: "new" must be matched before ":staffId".
                  { path: "staff", element: lazyRoute(<StaffListPage />) },
                  { path: "staff/new", element: lazyRoute(<StaffFormPage />) },
                  { path: "staff/:staffId", element: lazyRoute(<StaffProfilePage />) },
                  { path: "staff/:staffId/edit", element: lazyRoute(<StaffFormPage />) },
                  { path: "guardians", element: lazyRoute(<GuardianListPage />) },
                  { path: "guardians/:guardianId", element: lazyRoute(<GuardianProfilePage />) },
                  { path: "audit", element: lazyRoute(<AuditTrailPage />) },
                  { path: "notifications", element: lazyRoute(<NotificationsPage />) },
                  { path: "notifications/deliveries", element: lazyRoute(<DeliveryLogPage />) },
                  { path: "exams", element: lazyRoute(<ExamsPage />) },
                  { path: "exams/marks", element: lazyRoute(<MarksEntryPage />) },
                  { path: "fees", element: lazyRoute(<FeesDashboardPage />) },
                  { path: "fees/structures", element: lazyRoute(<FeeStructuresPage />) },
                  { path: "fees/invoices", element: lazyRoute(<InvoiceListPage />) },
                  { path: "ai", element: lazyRoute(<AssistantPage portal="admin" />) },
                  { path: "ai/suggestions", element: lazyRoute(<ProposalsPage />) },
                  { path: "ai/settings", element: lazyRoute(<AISettingsPage />) },
                  { path: "status", element: lazyRoute(<StatusPage />) },
                ],
              },
            ],
          },

          // --- Teaching portal ---------------------------------------------
          {
            element: <RequirePortal portal="teacher" />,
            children: [
              {
                path: "/teacher",
                element: <AppShell portal="teacher" />,
                children: [
                  { index: true, element: lazyRoute(<TeacherSectionsPage />) },
                  { path: "sections", element: lazyRoute(<TeacherSectionsPage />) },
                  { path: "attendance", element: lazyRoute(<MarkAttendancePage />) },
                  { path: "students", element: lazyRoute(<StudentListPage />) },
                  { path: "students/:studentId", element: lazyRoute(<StudentProfilePage />) },
                  { path: "marks", element: lazyRoute(<MarksEntryPage />) },
                  // The same page the administration portal mounts. Approving a
                  // practice question is a teacher's act — `practice.approve` is
                  // granted to the teacher role — but until now the only route
                  // to that screen was under /admin, which `RequirePortal`
                  // closes to them.
                  { path: "practice", element: lazyRoute(<PracticePage />) },
                  { path: "my-payslips", element: lazyRoute(<MyPayslipsPage />) },
                  { path: "timetable", element: lazyRoute(<TimetablePage />) },
                  { path: "announcements", element: lazyRoute(<AnnouncementsPage />) },
                  { path: "notifications", element: lazyRoute(<NotificationsPage />) },
                  { path: "ai", element: lazyRoute(<AssistantPage portal="teacher" />) },
                  { path: "ai/suggestions", element: lazyRoute(<ProposalsPage />) },
                  { path: "status", element: lazyRoute(<StatusPage />) },
                ],
              },
            ],
          },

          // --- Family portal (parents and students) -------------------------
          {
            element: <RequirePortal portal="parent" />,
            children: [
              {
                path: "/family",
                element: <AppShell portal="parent" />,
                children: [
                  { index: true, element: lazyRoute(<DashboardPage portal="parent" />) },
                  { path: "attendance", element: lazyRoute(<FamilyAttendancePage />) },
                  { path: "fees", element: lazyRoute(<FamilyFeesPage />) },
                  { path: "results", element: lazyRoute(<FamilyResultsPage />) },
                  { path: "practice", element: lazyRoute(<MyPracticePage />) },
                  { path: "practice/:attemptId", element: lazyRoute(<AttemptPage />) },
                  { path: "timetable", element: lazyRoute(<TimetablePage />) },
                  { path: "announcements", element: lazyRoute(<AnnouncementsPage />) },
                  { path: "notifications", element: lazyRoute(<NotificationsPage />) },
                  { path: "ai", element: lazyRoute(<AssistantPage portal="parent" />) },
                  { path: "status", element: lazyRoute(<StatusPage />) },
                ],
              },
            ],
          },
        ],
      },

      // Authenticated but not permitted. Distinct from /404, which is what an
      // out-of-scope *resource* returns — a 403 there would confirm the record
      // exists. docs/04-auth-authz.md §5.5
      { path: "/403", element: <ForbiddenPage /> },
      { path: "/404", element: <NotFoundPage /> },
      { path: "*", element: <Navigate to="/404" replace /> },
    ],
  },
]);
